import React, { useState } from "react";
import CreateNewSupervisor from "../APL/CreateNewSupervisor";
import { Button, Input, Modal } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import SupervisorPicker from "./SupervisorPicker";
import StudentPicker from "./StudentPicker";

const CreateAPLPeriodSupervisor = ({
  students,
  supervisors,
  aplStudentSupervisors,
  setAplStudentSupervisors,
  schoolId,
  programTeachers,
}) => {
  const intl = useIntl();
  const [addNewSupervisorVisible, setAddNewSupervisorVisible] = useState(false);
  const [supervisorToAddToSchool, setSupervisorToAddToSchool] = useState(null);
  const [addToSchoolIsOpen, setAddToSchoolIsOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState(() => new Set());
  const [addStudentPickerOpen, setAddStudentPickerOpen] = useState(false);
  const [bulkAssignPickerOpen, setBulkAssignPickerOpen] = useState(false);
  const [assignSupervisorRowId, setAssignSupervisorRowId] = useState(null);
  const [pendingNewSupervisorTarget, setPendingNewSupervisorTarget] =
    useState(null);

  const queryClient = useQueryClient();

  const addSupervisorToList = (list, stud, supervisor) => {
    const idx = list.findIndex((s) => s.id === stud.id);

    if (idx === -1) {
      return [
        ...list,
        { ...stud, supervisors: [{ ...supervisor, isMainResponsible: true }] },
      ];
    }

    const existing = list[idx];
    if (existing.supervisors.some((sv) => sv.id === supervisor.id)) {
      return list;
    }

    const newSup =
      existing.supervisors.length === 0
        ? { ...supervisor, isMainResponsible: true }
        : { ...supervisor };
    const updated = {
      ...existing,
      supervisors: [...existing.supervisors, newSup],
    };
    return list.map((s, i) => (i === idx ? updated : s));
  };

  const handleAddSupervisor = (stud, supervisor) => {
    setAplStudentSupervisors((prev) =>
      addSupervisorToList(prev, stud, supervisor.value),
    );
  };

  const handleReloadSupervisors = () => {
    queryClient.invalidateQueries({ queryKey: ["FetchSupervisors"] });
  };

  const toggleAddToSchool = () => {
    setAddToSchoolIsOpen(!addToSchoolIsOpen);
  };

  const handleRemoveSupervisorFromStudent = (supervisorId, studentId) => {
    setAplStudentSupervisors((prev) =>
      prev.map((stud) => {
        if (stud.id !== studentId) return stud;
        const filtered = stud.supervisors.filter(
          (sv) => sv.id !== supervisorId,
        );
        const hasMain = filtered.some((sv) => sv.isMainResponsible);
        const supervisors =
          filtered.length === 0 || hasMain
            ? filtered
            : filtered.map((sv, i) =>
                i === 0 ? { ...sv, isMainResponsible: true } : sv,
              );
        return { ...stud, supervisors };
      }),
    );
  };

  const handleRemoveStudentFromAplStudents = (student) => {
    setAplStudentSupervisors(
      aplStudentSupervisors.filter((stud) => stud.id !== student.id),
    );
  };

  const handleSetSupervisorResponsable = (studentId, supervisorId) => {
    setAplStudentSupervisors((prev) =>
      prev.map((stud) =>
        stud.id !== studentId
          ? stud
          : {
              ...stud,
              supervisors: stud.supervisors.map((sv) => ({
                ...sv,
                isMainResponsible: sv.id === supervisorId,
              })),
            },
      ),
    );
  };

  const toggleSelectStudent = (studentId) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      next.has(studentId) ? next.delete(studentId) : next.add(studentId);
      return next;
    });
  };

  const handleToggleAllStudents = (visibleStudentIds, allSelected) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      visibleStudentIds.forEach((id) => {
        allSelected ? next.delete(id) : next.add(id);
      });
      return next;
    });
  };

  const handleAddStudentsFromPicker = (studentObjects) => {
    setAplStudentSupervisors((prev) => {
      const existingIds = new Set(prev.map((s) => s.id));
      const toAdd = studentObjects.filter((s) => !existingIds.has(s.id));
      return [...toAdd.map((stud) => ({ ...stud, supervisors: [] })), ...prev];
    });
    const ids = studentObjects.map((s) => s.id);
    setSelectedStudentIds((prev) => new Set([...ids, ...prev]));
  };

  const handleBulkAssignSupervisor = (supervisor) => {
    setAplStudentSupervisors((prev) => {
      let next = prev;
      selectedStudentIds.forEach((sid) => {
        const stud = next.find((s) => s.id === sid);
        if (stud) next = addSupervisorToList(next, stud, supervisor);
      });
      return next;
    });
  };

  const handleRemoveStudentFromRow = (student) => {
    handleRemoveStudentFromAplStudents(student);
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      next.delete(student.id);
      return next;
    });
  };

  const handleSupervisorCreatedAutoAssign = (createdUser) => {
    if (!pendingNewSupervisorTarget) return;
    setAplStudentSupervisors((prev) => {
      let next = prev;
      pendingNewSupervisorTarget.eids.forEach((sid) => {
        const stud = next.find((s) => s.id === sid);
        if (stud) next = addSupervisorToList(next, stud, createdUser);
      });
      return next;
    });
    setPendingNewSupervisorTarget(null);
  };

  const selectableStudents = students.filter(
    (s) => !aplStudentSupervisors.some((a) => a.id === s.id),
  );

  const TeacherTag = () => (
    <span className="aplmod-teacher-tag">
      <FormattedMessage id="TeacherSuffix" />
    </span>
  );

  const SupervisorAffix = ({ supervisor }) => {
    if (supervisor.teacher) return <TeacherTag />;
    if (supervisor.aplCompany?.name) {
      return (
        <span className="aplmod-teacher-tag">
          ({supervisor.aplCompany.name})
        </span>
      );
    }
    return "";
  };

  const SupervisorEditRow = ({ supervisor, studentId, isAnsv }) => (
    <div className="aplmod-supervisor-edit-row d-flex align-items-center gap-2">
      <button
        type="button"
        className="aplmod-supervisor-edit-row__remove"
        onClick={() =>
          handleRemoveSupervisorFromStudent(supervisor.id, studentId)
        }
        title={intl.formatMessage({ id: "RemoveSupervisor" })}
        aria-label={intl.formatMessage({ id: "RemoveSupervisor" })}
      >
        <FontAwesomeIcon icon="trash" />
      </button>
      <span
        className={`aplmod-supervisor-edit-row__star${
          isAnsv ? "" : " aplmod-supervisor-edit-row__star--inactive"
        }`}
        onClick={() => {
          if (!isAnsv)
            handleSetSupervisorResponsable(studentId, supervisor.id);
        }}
        title={intl.formatMessage({
          id: isAnsv
            ? "OneSupervisorHasToBeResponsableInfo"
            : "SetAsMainResponsible",
        })}
      >
        <FontAwesomeIcon icon="star" />
      </span>
      <span className="aplmod-supervisor-edit-row__name">
        {supervisor.name}
        <SupervisorAffix supervisor={supervisor} />
      </span>
    </div>
  );

  const renderStudentRow = (stud) => {
    const sups = stud.supervisors || [];
    const isSelected = selectedStudentIds.has(stud.id);
    const ansvSup = sups.find((s) => s.isMainResponsible) || sups[0];
    const rowAddBtnId = `aplmod-add-${stud.id}`;
    const isPickerOpenForRow = assignSupervisorRowId === stud.id;

    return (
      <div
        key={stud.id}
        className="aplmod-row d-flex align-items-center px-3"
        style={{
          background: isSelected ? "#eaf6fb" : "transparent",
        }}
      >
        <div className="aplmod-cell-checkbox">
          <Input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelectStudent(stud.id)}
          />
        </div>
        <div
          className="aplmod-cell-name"
          style={{ fontWeight: 500 }}
          title={stud.name}
        >
          {stud.name}
        </div>

        {sups.length === 0 ? (
          <div className="aplmod-supervisor-col d-flex align-items-center">
            <span style={{ color: "#adb5bd", fontStyle: "italic" }}>
              <FormattedMessage id="NoSupervisor" />
            </span>
          </div>
        ) : (
          <div className="aplmod-supervisor-col d-flex flex-column gap-1 py-2">
            {sups.map((sv) => (
              <SupervisorEditRow
                key={sv.id}
                supervisor={sv}
                studentId={stud.id}
                isAnsv={sv === ansvSup}
              />
            ))}
          </div>
        )}

        <div className="aplmod-row-actions d-flex align-items-center gap-1 ms-2 flex-shrink-0">
          <Button
            id={rowAddBtnId}
            outline
            color="primary"
            size="sm"
            className="aplmod-row-icon-btn"
            onClick={() =>
              setAssignSupervisorRowId(isPickerOpenForRow ? null : stud.id)
            }
            title={intl.formatMessage({ id: "AddSupervisor" })}
          >
            +
          </Button>
          <Button
            outline
            color="danger"
            size="sm"
            className="aplmod-row-icon-btn"
            onClick={() => handleRemoveStudentFromRow(stud)}
            title={intl.formatMessage({ id: "RemoveStudentFromPeriod" })}
            aria-label={intl.formatMessage({ id: "RemoveStudentFromPeriod" })}
          >
            ×
          </Button>
        </div>

        {isPickerOpenForRow ? (
          <SupervisorPicker
            target={rowAddBtnId}
            isOpen={isPickerOpenForRow}
            toggle={() => setAssignSupervisorRowId(null)}
            supervisors={supervisors}
            programTeachers={programTeachers}
            excludedIds={sups.map((s) => s.id)}
            onPick={(sup) => handleAddSupervisor(stud, { value: sup })}
            onAddNew={() => {
              setPendingNewSupervisorTarget({
                type: "single",
                studentName: stud.name,
                eids: [stud.id],
              });
              setAddNewSupervisorVisible(true);
            }}
          />
        ) : (
          ""
        )}
      </div>
    );
  };

  const lq = studentSearch.toLowerCase();
  const inPeriodList = aplStudentSupervisors.filter((s) => {
    if (!lq) return true;
    if (s.name.toLowerCase().includes(lq)) return true;
    return (s.supervisors || []).some((sv) =>
      (sv.name || "").toLowerCase().includes(lq),
    );
  });
  const visibleInPeriodIds = inPeriodList.map((s) => s.id);
  const inPeriodAllSelected =
    visibleInPeriodIds.length > 0 &&
    visibleInPeriodIds.every((id) => selectedStudentIds.has(id));
  const inPeriodAnySelected = visibleInPeriodIds.some((id) =>
    selectedStudentIds.has(id),
  );

  return (
    <>
      {/* Toolbar: search + add student */}
      <div className="aplmod-toolbar d-flex align-items-center gap-2">
        <Input
          value={studentSearch}
          onChange={(e) => setStudentSearch(e.target.value)}
          placeholder={intl.formatMessage({ id: "SearchStudentOrSupervisor" })}
          className="flex-fill aplmod-search-input"
        />
        <Button
          id="aplmod-add-student-link"
          color="success"
          disabled={selectableStudents.length === 0}
          onClick={() => setAddStudentPickerOpen((v) => !v)}
          style={{ whiteSpace: "nowrap" }}
          title={
            selectableStudents.length === 0
              ? intl.formatMessage({ id: "NoMoreStudentsToAdd" })
              : undefined
          }
        >
          <FontAwesomeIcon icon="user-plus" className="me-1" />
          <FormattedMessage
            id={
              selectableStudents.length > 0
                ? "AddStudentWithCount"
                : "AddStudent"
            }
            values={{ count: selectableStudents.length }}
          />
        </Button>
      </div>

      {/* Bulk action bar */}
      {selectedStudentIds.size > 0 ? (
        <div
          className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
          style={{ background: "#eaf6fb" }}
        >
          <strong style={{ whiteSpace: "nowrap" }}>
            <FormattedMessage
              id={
                selectedStudentIds.size === 1
                  ? "NStudentSelected"
                  : "NStudentsSelected"
              }
              values={{ count: selectedStudentIds.size }}
            />
          </strong>
          <Button
            id="aplmod-bulk-assign-btn"
            outline
            color="primary"
            onClick={() => setBulkAssignPickerOpen((v) => !v)}
          >
            <FormattedMessage id="AssignSupervisor" /> ▾
          </Button>
          {bulkAssignPickerOpen ? (
            <SupervisorPicker
              target="aplmod-bulk-assign-btn"
              isOpen={bulkAssignPickerOpen}
              toggle={() => setBulkAssignPickerOpen(false)}
              supervisors={supervisors}
              programTeachers={programTeachers}
              excludedIds={[]}
              onPick={(sup) => handleBulkAssignSupervisor(sup)}
              onAddNew={() => {
                setPendingNewSupervisorTarget({
                  type: "bulk",
                  count: selectedStudentIds.size,
                  eids: [...selectedStudentIds],
                });
                setAddNewSupervisorVisible(true);
              }}
            />
          ) : (
            ""
          )}
          <Button
            outline
            color="danger"
            onClick={() => {
              const idsToRemove = new Set(selectedStudentIds);
              setAplStudentSupervisors((prev) =>
                prev.filter((s) => !idsToRemove.has(s.id)),
              );
              setSelectedStudentIds(new Set());
            }}
          >
            <FormattedMessage id="RemoveFromPeriod" />
          </Button>
        </div>
      ) : (
        ""
      )}

      {addStudentPickerOpen ? (
        <StudentPicker
          target="aplmod-add-student-link"
          isOpen={addStudentPickerOpen}
          toggle={() => setAddStudentPickerOpen(false)}
          students={selectableStudents}
          onAdd={(picked) => handleAddStudentsFromPicker(picked)}
        />
      ) : (
        ""
      )}

      {/* Column header row — matches geometry of renderStudentRow */}
      <div className="aplmod-col-header d-flex align-items-center px-3">
        <div className="aplmod-cell-checkbox">
          <Input
            type="checkbox"
            checked={inPeriodAllSelected}
            innerRef={(el) => {
              if (el)
                el.indeterminate = inPeriodAnySelected && !inPeriodAllSelected;
            }}
            onChange={() =>
              handleToggleAllStudents(visibleInPeriodIds, inPeriodAllSelected)
            }
            title={intl.formatMessage({
              id: inPeriodAllSelected ? "DeselectAll" : "SelectAllStudents",
            })}
          />
        </div>
        <div className="aplmod-cell-name">
          <FormattedMessage id="Name" />
          <span className="aplmod-section-count">({inPeriodList.length})</span>
        </div>
        <div className="aplmod-supervisor-col">
          <FormattedMessage id="Supervisor" />
        </div>
        <div className="aplmod-col-header__edit ms-2 flex-shrink-0">
          <FormattedMessage id="Edit" />
        </div>
      </div>

      {inPeriodList.map((stud) => renderStudentRow(stud))}

      {inPeriodList.length === 0 && !studentSearch ? (
        <div className="text-center text-muted fst-italic py-4 small">
          <FormattedMessage id="NoStudentsInPeriodYet" />
        </div>
      ) : (
        ""
      )}

      <Modal
        isOpen={addNewSupervisorVisible}
        toggle={() => setAddNewSupervisorVisible(false)}
        centered
      >
        <CreateNewSupervisor
          dismissModal={() => setAddNewSupervisorVisible(false)}
          handleReloadSupervisors={handleReloadSupervisors}
          supervisorToAddToSchool={supervisorToAddToSchool}
          setSupervisorToAddToSchool={setSupervisorToAddToSchool}
          addToSchoolIsOpen={addToSchoolIsOpen}
          setAddToSchoolIsOpen={setAddToSchoolIsOpen}
          toggleAddToSchool={toggleAddToSchool}
          schoolId={schoolId}
          context={pendingNewSupervisorTarget}
          onSupervisorCreated={handleSupervisorCreatedAutoAssign}
        />
      </Modal>
    </>
  );
};
export default CreateAPLPeriodSupervisor;
